import { Injectable } from '@angular/core';
import {
  APIService,
  AssignGeofenceInput,
  GeofenceEnforcedAction,
  GeofenceView,
} from 'app/API.service.custom';
import { fetchAllPages } from '../pagination/fetch-all-pages';
import { Geofence, GeofenceMember, GeofenceRevision } from './geofence.model';

/**
 * Service layer for geofence administration, backed by the `geofenceAdminService`
 * lambda.
 *
 * Three things about that contract shape everything here:
 *
 *  1. **Assignment lives only in this service.** The geofence fields were removed
 *     from both `UpdateAccountMembershipInput` and `UpdateAccountMembershipDataInput`,
 *     so `assignGeofenceToMember` / `unassignGeofenceFromMember` are the only way
 *     in. All writes are SUPERADMIN-gated server-side.
 *  2. **Two ids.** Writes address a geofence by `logicalGeofenceId`;
 *     `AccountMembership.operatingGeofenceId` points at a REVISION `id`. Matching a
 *     member to a geofence therefore has to allow for both — see `matchesMember`.
 *  3. **Errors arrive as a message prefix**, not `extensions.code` — e.g.
 *     `"GEOFENCE_IN_USE: 3 members are still assigned"`. `parseErrorCode` reads it.
 */

/** Server-side caps, mirrored so the editor can warn before a rejected save. */
export const GEOFENCE_SERVER_VERTEX_CAP = 2000;
export const GEOFENCE_SERVER_GEOMETRY_BYTES = 256 * 1024;

export interface GeofenceServiceError {
  code: string | null;
  message: string;
}

export interface GeofenceBulkAssignResult {
  succeeded: string[];
  failed: Array<{ membershipId: string; name: string; message: string }>;
}

/**
 * Error codes come back as a `CODE: message` (or `CODE:{json}`) prefix on the
 * GraphQL error message rather than in `extensions.code`.
 */
export function parseErrorCode(error: any): GeofenceServiceError {
  const raw: string = error?.errors?.[0]?.message || error?.message || '';
  const match = /^([A-Z][A-Z0-9_]{2,}):\s*(.*)$/s.exec(raw);
  if (!match) {
    return { code: null, message: raw || 'The server rejected the request. Nothing was changed.' };
  }
  return { code: match[1], message: match[2].trim() || raw };
}

/**
 * Decodes the geometry string.
 *
 * Loops while the result is still a string: an `AWSJSON` field holding an
 * already-stringified value comes back double-encoded, and one `JSON.parse` then
 * yields a string rather than the object. That parses "successfully" and silently
 * produces a geofence with no shape, so unwrap until it stops being a string
 * (bounded, so a pathological value cannot spin).
 */
function parseGeometry(raw: string): any {
  let value: any = raw;
  for (let attempt = 0; attempt < 3 && typeof value === 'string'; attempt++) {
    try {
      value = JSON.parse(value);
    } catch {
      // A geofence whose geometry will not parse must not take down the whole
      // list; it renders as an empty shape and the rest of the page still works.
      console.error('Geofence geometry is not valid JSON', raw);
      return null;
    }
  }
  return typeof value === 'string' ? null : value;
}

function toGeofence(view: GeofenceView): Geofence {
  const geometry = parseGeometry(view.geometry);
  if (geometry && geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') {
    // Anything else (a Feature wrapper, a GeometryCollection) renders as an empty
    // map with no clue why, so name the shape we actually got.
    console.warn(
      `Geofence "${view.name}" has geometry of type "${geometry.type}"; only Polygon and MultiPolygon can be drawn.`,
      geometry,
    );
  }
  return {
    id: view.id,
    logicalGeofenceId: view.logicalGeofenceId,
    accountId: view.accountId,
    name: view.name,
    version: view.version,
    status: view.status,
    geometry,
    supersededById: view.supersededById,
    assignedMemberCount: view.assignedMemberCount,
    createdAt: view.createdAt,
    updatedAt: view.updatedAt,
  };
}

@Injectable({ providedIn: 'root' })
export class GeofenceAdminService {
  constructor(private api: APIService) {}

  // -------------------------------------------------------------- geofences

  /** ACTIVE revisions only — one row per geofence. */
  async listGeofences(accountId: string): Promise<Geofence[]> {
    const views = (await this.api.ListAccountGeofences(accountId)) || [];
    return views.filter(Boolean).map(toGeofence);
  }

  /**
   * Version history for one geofence.
   *
   * There is no revisions query. `listAccountGeofences(includeSuperseded: true)`
   * returns every revision on the account, so history is that list filtered to
   * one `logicalGeofenceId`, newest first.
   */
  async listRevisions(accountId: string, logicalGeofenceId: string): Promise<GeofenceRevision[]> {
    const views = (await this.api.ListAccountGeofences(accountId, true)) || [];
    return views
      .filter(v => v && v.logicalGeofenceId === logicalGeofenceId)
      .map(toGeofence)
      .sort((a, b) => b.version - a.version);
  }

  async createGeofence(accountId: string, name: string, geometry: unknown): Promise<Geofence> {
    return toGeofence(
      await this.api.CreateGeofence({ accountId, name, geometry: JSON.stringify(geometry) }),
    );
  }

  /**
   * Publishes a NEW revision. The server bumps `version`, supersedes the previous
   * revision and re-points assigned members. Attendance and visits already open
   * are untouched.
   */
  async reviseGeofence(
    accountId: string,
    logicalGeofenceId: string,
    name: string,
    geometry: unknown,
  ): Promise<Geofence> {
    return toGeofence(
      await this.api.ReviseGeofence({
        accountId,
        logicalGeofenceId,
        name,
        geometry: JSON.stringify(geometry),
      }),
    );
  }

  /** Rejected with `GEOFENCE_IN_USE` while any member is still assigned. */
  async archiveGeofence(accountId: string, logicalGeofenceId: string): Promise<Geofence> {
    return toGeofence(await this.api.ArchiveGeofence(accountId, logicalGeofenceId));
  }

  // ---------------------------------------------------------------- members

  /** Every membership on the account, with its current geofence assignment. */
  async listMembers(accountId: string): Promise<GeofenceMember[]> {
    const items = await fetchAllPages(
      nextToken => this.api.UsersByAccountForGeofence(accountId, undefined, undefined, undefined, 500, nextToken),
      'UsersByAccountForGeofence',
    );

    return items.map(item => ({
      membershipId: item.id,
      userId: item.userId,
      name: item.user?.name || item.designation || 'Unnamed member',
      designation: item.designation,
      authorizationLevel: item.authorizationLevel,
      userAccountStatus: item.userAccountStatus,
      profilePictureUrl: item.user?.profilePictureUrl,
      operatingGeofenceId: item.operatingGeofenceId,
      geofenceEnforcedActions: (item.geofenceEnforcedActions || []).filter(
        (a): a is GeofenceEnforcedAction => a != null,
      ),
    }));
  }

  /**
   * Does this member sit on this geofence?
   *
   * `operatingGeofenceId` holds a revision `id`, and a revise re-points members
   * onto the new revision — so matching the ACTIVE revision's `id` is the normal
   * case. `logicalGeofenceId` is accepted too, so a member left pointing at the
   * logical id still resolves to the right geofence rather than silently reading
   * as unassigned.
   */
  matchesMember(geofence: Geofence, member: GeofenceMember): boolean {
    const assigned = member.operatingGeofenceId;
    if (!assigned) return false;
    return assigned === geofence.id || assigned === geofence.logicalGeofenceId;
  }

  /**
   * Attach a member to a geofence and set which actions it gates.
   *
   * One geofence per member: this REPLACES any existing assignment. Reassignment
   * applies prospectively and never closes an open attendance or visit.
   */
  async assignMember(input: AssignGeofenceInput): Promise<void> {
    await this.api.AssignGeofenceToMember(input);
  }

  /**
   * Assign several members to one geofence.
   *
   * There is no bulk operation on the backend, so this is N calls and therefore
   * NOT atomic — some can succeed while others fail. The caller gets a per-member
   * result rather than a single throw, so the UI can say exactly who did not make
   * it and why instead of implying the whole batch failed.
   *
   * Runs a few at a time: sequential is needlessly slow for a large team, and
   * firing all of them at once risks throttling the lambda.
   */
  async assignMembers(
    accountId: string,
    logicalGeofenceId: string,
    members: Array<{ membershipId: string; name: string; enforcedActions: GeofenceEnforcedAction[] }>,
    onProgress?: (done: number, total: number) => void,
  ): Promise<GeofenceBulkAssignResult> {
    const succeeded: string[] = [];
    const failed: Array<{ membershipId: string; name: string; message: string }> = [];
    const queue = [...members];
    let done = 0;

    const worker = async (): Promise<void> => {
      for (;;) {
        const member = queue.shift();
        if (!member) return;
        try {
          await this.assignMember({
            accountId,
            membershipId: member.membershipId,
            logicalGeofenceId,
            enforcedActions: member.enforcedActions,
          });
          succeeded.push(member.membershipId);
        } catch (error) {
          failed.push({
            membershipId: member.membershipId,
            name: member.name,
            message: parseErrorCode(error).message,
          });
        } finally {
          done += 1;
          onProgress?.(done, members.length);
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(4, members.length) }, () => worker()));
    return { succeeded, failed };
  }

  /**
   * Detach a member's geofence.
   *
   * The server clears the enforced actions in the same write, so a member can
   * never be left with actions and nothing to check them against.
   */
  async unassignMember(accountId: string, membershipId: string): Promise<void> {
    await this.api.UnassignGeofenceFromMember(accountId, membershipId);
  }

  /**
   * The CALLER's own policy — a member-scoped read, not an admin lookup of
   * someone else. Feeds a rep-facing "where can I check in?" view.
   */
  async myPolicy(accountId: string) {
    const policy = await this.api.MyGeofencePolicy(accountId);
    if (!policy) return null;
    return { ...policy, geometry: parseGeometry(policy.geometry) };
  }
}
